import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import Container from '@material-ui/core/Container';
import useEntries from '../Common/useEntries';
import Publication from './Publication';
import { byLatestUpload } from './newPublications';
import PublicationSidebar from './PublicationSidebar';
import Pager from '../Common/Pager';
import {
    PageHero,
    LoadingState,
    EmptyState,
    FilterBar,
    FilterLabel,
} from '../Common/PageLayout';

const PER_PAGE = 12;
const UNCATEGORISED = 'Uncategorised';

const breadcrumbs = [{ label: 'Publications' }];

/**
 * Searchable, faceted archive of the Ministry's publications. On a directorate subdomain,
 * `directorateSlug` limits it to publications owned by that directorate (`filesowner`).
 */
export default function PublicationList({ directorateSlug } = {}) {
    const { items, loading, error } = useEntries({
        content_type: 'publications',
        ...(directorateSlug ? { 'fields.filesowner.slug': directorateSlug } : {}),
        order: '-sys.createdAt',
        limit: 100,
    }, { all: true });

    const [term, setTerm] = useState('');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(1);

    // The CMS is asked for newest first; sort here too so the latest upload always leads the list.
    const latestFirst = useMemo(() => [...items].sort(byLatestUpload), [items]);

    const searchMatches = useMemo(() => {
        const needle = term.trim().toLowerCase();
        if (!needle) return latestFirst;
        return latestFirst.filter((entry) =>
            (entry.fields?.title || '').toLowerCase().includes(needle)
        );
    }, [latestFirst, term]);

    const categories = useMemo(() => {
        const counts = new Map();
        searchMatches.forEach((entry) => {
            const name = entry.fields?.publicationCategory || UNCATEGORISED;
            counts.set(name, (counts.get(name) || 0) + 1);
        });
        return Array.from(counts, ([name, count]) => ({ name, count })).sort(
            (a, b) => b.count - a.count || a.name.localeCompare(b.name)
        );
    }, [searchMatches]);

    const filtered = useMemo(() => {
        if (selected.length === 0) return searchMatches;
        return searchMatches.filter((entry) =>
            selected.includes(entry.fields?.publicationCategory || UNCATEGORISED)
        );
    }, [searchMatches, selected]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const visible = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

    const toggleCategory = (name) => {
        setSelected((previous) =>
            previous.includes(name)
                ? previous.filter((value) => value !== name)
                : [...previous, name]
        );
        setPage(1);
    };

    return (
        <>
            <Helmet>
                <title>Publications | Ministry of Finance, Timor-Leste</title>
                <meta
                    name="description"
                    content="Budget books, financial statements, reports and other official publications of the Ministry of Finance of Timor-Leste."
                />
            </Helmet>

            <PageHero
                eyebrow="Documents"
                title="Publications"
                description="Budget books, execution reports, financial statements, policy documents and guidance issued by the Ministry of Finance."
                breadcrumbs={breadcrumbs}
            />

            <div style={{ paddingTop: 48, paddingBottom: 64 }}>
                <Container maxWidth="lg">
                    <Grid container spacing={5}>
                        <Grid item xs={12} md={3}>
                            <PublicationSidebar
                                categories={categories}
                                selectedCategories={selected}
                                onToggle={toggleCategory}
                                onClear={() => {
                                    setSelected([]);
                                    setPage(1);
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={9}>
                            <FilterBar
                                resultCount={loading || error ? null : filtered.length}
                                resultNoun="publications"
                            >
                                <FilterLabel>Search</FilterLabel>
                                <TextField
                                    id="publication-search"
                                    variant="outlined"
                                    size="small"
                                    placeholder="Search by title"
                                    value={term}
                                    onChange={(event) => {
                                        setTerm(event.target.value);
                                        setPage(1);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="disabled" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    style={{ minWidth: 280 }}
                                />
                            </FilterBar>

                            {loading && <LoadingState label="Loading publications" />}

                            {!loading && error && (
                                <div role="alert">
                                    <EmptyState
                                        title="Unable to load all publications"
                                        description="The publication archive could not be loaded completely. Please refresh the page to try again."
                                    />
                                </div>
                            )}

                            {!loading && !error && visible.length === 0 && (
                                <EmptyState
                                    title="No publications match your filters"
                                    description="Try a different search term or clear the selected categories."
                                />
                            )}

                            {!loading && visible.length > 0 && (
                                <>
                                    <Grid container spacing={3}>
                                        {visible.map((entry) => (
                                            <Grid item xs={12} sm={6} lg={4} key={entry.sys.id}>
                                                <Publication publications={entry} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                    <Pager
                                        page={safePage}
                                        totalPages={totalPages}
                                        onChange={setPage}
                                    />
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Container>
            </div>
        </>
    );
}
